const MAX_EVENTS = 50;
const EVENT_TTL_MS = 24 * 60 * 60 * 1000;
const POST_TIMEOUT_MS = 15_000;
const EVENT_TYPES = new Set([
  'auth_refresh',
  'auth_lock_timeout',
  'auth_lock_unavailable',
  'auth_request_retry',
  'auth_callback_slow',
  'auth_callback_result',
  'auth_interactive_redirect',
]);
const ALLOWED_METADATA = new Set([
  'outcome',
  'reason',
  'durationMs',
  'lockWaitMs',
  'errorCategory',
  'contextId',
  'flowId',
  'isTopLevel',
  'visibility',
  'online',
  'appVersion',
  'occurredAt',
]);

// Index keeps identical legacy records separate while a crashed import retries.
function legacyId(event, index) {
  let hash = 2166136261;
  for (const char of JSON.stringify(event)) {
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  }
  return `legacy-${index}-${(hash >>> 0).toString(16)}`;
}

function sampleSuccessfulFlow(flowId) {
  let hash = 2166136261;
  for (const char of flowId) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return (hash >>> 0) % 10 === 0;
}

export function createAuthTelemetry(options) {
  const prefix = `beedigit:auth-events:v2:${options.source}:`;
  const v1Key = `beedigit:auth-events:v1:${options.source}`;
  const contextId = options.random();
  const ownPrefix = `${prefix}${contextId}:`;
  const locks = options.locks === undefined ? navigator.locks : options.locks;
  let sequence = 0;
  let flushInFlight = null;

  const keys = () => {
    const found = [];
    try {
      for (let i = 0; i < options.storage.length; i += 1) {
        const key = options.storage.key(i);
        if (key?.startsWith(prefix)) found.push(key);
      }
    } catch {
      /* Storage may be unavailable; auth must continue. */
    }
    return found;
  };
  const remove = (key) => {
    try {
      options.storage.removeItem(key);
    } catch {
      /* Retry cleanup later. */
    }
  };
  const read = (key) => {
    let raw;
    try {
      raw = options.storage.getItem(key);
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      const envelope = JSON.parse(raw);
      if (
        !envelope ||
        typeof envelope.id !== 'string' ||
        !envelope.event ||
        typeof envelope.event.occurredAt !== 'string' ||
        !Number.isFinite(Date.parse(envelope.event.occurredAt))
      ) {
        remove(key);
        return null;
      }
      if (options.now() - Date.parse(envelope.event.occurredAt) > EVENT_TTL_MS) {
        remove(key);
        return null;
      }
      return { key, envelope, raw };
    } catch {
      remove(key);
      return null;
    }
  };
  const contextEntries = (context) =>
    keys()
      .filter((key) => key.startsWith(`${prefix}${context}:`))
      .map(read)
      .filter(Boolean)
      .sort(
        (a, b) =>
          a.envelope.event.occurredAt.localeCompare(b.envelope.event.occurredAt) ||
          (a.envelope.order ?? 0) - (b.envelope.order ?? 0) ||
          a.envelope.id.localeCompare(b.envelope.id),
      );
  const prune = (context) => {
    const entries = contextEntries(context);
    for (const entry of entries.slice(0, Math.max(0, entries.length - MAX_EVENTS)))
      remove(entry.key);
  };
  const sanitize = (metadata) =>
    Object.fromEntries(Object.entries(metadata).filter(([key]) => ALLOWED_METADATA.has(key)));

  const recordAuthEvent = (eventType, metadata = {}) => {
    const occurredAt = new Date(options.now()).toISOString();
    const flowId = typeof metadata.flowId === 'string' ? metadata.flowId : options.random();
    if (
      !options.development &&
      metadata.outcome === 'success' &&
      (eventType === 'auth_refresh' || eventType === 'auth_request_retry') &&
      !(typeof metadata.lockWaitMs === 'number' && metadata.lockWaitMs > 1000) &&
      !sampleSuccessfulFlow(flowId)
    ) {
      return flowId;
    }
    const event = {
      eventType,
      source: options.source,
      pageTitle: 'auth',
      pagePath: options.pathname().split(/[?#]/, 1)[0] || '/',
      occurredAt,
      metadata: sanitize({
        ...metadata,
        contextId,
        flowId,
        isTopLevel: window.top === window,
        visibility: document.visibilityState,
        online: navigator.onLine,
        occurredAt,
      }),
    };
    const order = sequence++;
    const id = window.crypto?.randomUUID?.() ?? `${options.random()}-${options.now()}-${order}`;
    try {
      options.storage.setItem(`${ownPrefix}${id}`, JSON.stringify({ id, order, event }));
      prune(contextId);
    } catch {
      /* A full or denied store must not interrupt authentication. */
    }
    if (options.development) console.debug('[auth]', event);
    return flowId;
  };

  const post = async (event, accessToken) => {
    const controller = new AbortController();
    let timer;
    try {
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error('telemetry timeout'));
        }, POST_TIMEOUT_MS);
      });
      const response = await Promise.race([
        options.fetch(options.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(event),
          signal: controller.signal,
        }),
        timeout,
      ]);
      return response.ok;
    } catch {
      return false;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };
  const drain = async (context, accessToken) => {
    if (context === 'legacy-import') {
      try {
        if (options.storage.getItem(v1Key) !== null) return;
      } catch {
        return;
      }
    }
    for (const entry of contextEntries(context)) {
      if (!(await post(entry.envelope.event, accessToken))) break;
      try {
        if (options.storage.getItem(entry.key) === entry.raw) remove(entry.key);
      } catch {
        /* Delivery is at least once when local acknowledgement fails. */
      }
    }
  };
  const queueLock = (context) => `beedigit:auth-events-flush:v2:${options.source}:${context}`;
  const migrate = async () => {
    // Set legacyWritersRetired only through a release-controlled opt-in after every v1 tab/build
    // has retired or reloaded. A healthy authenticated flush with Web Locks then imports v1.
    // The runtime singleton leaves this false during mixed-version rollout.
    if (!options.legacyWritersRetired || !locks) return;
    try {
      await locks.request(queueLock('legacy-import'), async () => {
        const raw = options.storage.getItem(v1Key);
        if (raw === null) return;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return;
        const valid = parsed
          .map((item, index) => ({ item, index }))
          .filter(
            ({ item }) =>
              item &&
              EVENT_TYPES.has(item.eventType) &&
              item.source === options.source &&
              typeof item.pagePath === 'string' &&
              typeof item.occurredAt === 'string' &&
              Number.isFinite(Date.parse(item.occurredAt)) &&
              options.now() - Date.parse(item.occurredAt) <= EVENT_TTL_MS,
          );
        for (const { item: legacy, index } of valid.slice(-MAX_EVENTS)) {
          const event = {
            eventType: legacy.eventType,
            source: options.source,
            pageTitle: 'auth',
            pagePath: legacy.pagePath.split(/[?#]/, 1)[0] || '/',
            occurredAt: legacy.occurredAt,
            metadata: sanitize(
              legacy.metadata &&
                typeof legacy.metadata === 'object' &&
                !Array.isArray(legacy.metadata)
                ? legacy.metadata
                : {},
            ),
          };
          const id = legacyId(event, index);
          const key = `${prefix}legacy-import:${id}`;
          if (options.storage.getItem(key) === null)
            options.storage.setItem(key, JSON.stringify({ id, order: index, event }));
        }
        options.storage.removeItem(v1Key);
      });
    } catch {
      /* Leave v1 untouched; deterministic IDs make retry safe. */
    }
  };
  const doFlush = async (accessToken) => {
    await migrate();
    if (!locks) {
      await drain(contextId, accessToken);
      return;
    }
    const contexts = new Set(
      keys()
        .map((key) => key.slice(prefix.length).split(':', 1)[0])
        .filter(Boolean),
    );
    for (const context of contexts) {
      try {
        await locks.request(queueLock(context), () => drain(context, accessToken));
      } catch {
        /* No unlocked send after a lock failure. */
      }
    }
  };
  const flushAuthEvents = (accessToken) => {
    if (flushInFlight) return flushInFlight;
    const pending = doFlush(accessToken).catch(() => undefined);
    flushInFlight = pending.finally(() => {
      flushInFlight = null;
    });
    return flushInFlight;
  };
  return { recordAuthEvent, flushAuthEvents };
}

export const authTelemetry = createAuthTelemetry({
  source: 'supervisor-webapp',
  endpoint: `${process.env.VUE_APP_API_SERVER_PATH}api/v1/user-activity/events`,
  storage: localStorage,
  fetch: window.fetch.bind(window),
  now: Date.now,
  random: () => crypto.randomUUID(),
  pathname: () => window.location.pathname,
  development: process.env.NODE_ENV !== 'production',
});
